import React from 'react';
import PropTypes from 'prop-types';
import {
  Flex, Tooltip, TooltipTrigger,
} from '@adobe/react-spectrum';
import GlobalData from '../utils/GlobalData';
import PostMessageUtil from '../utils/PostMessageUtil';

const EXPANDED_WIDTH_THRESHOLD = 500;

export default function Header(props) {
  const OUTGOING_MESSAGES_TYPE = PostMessageUtil.outgoingMessageTypes;
  const { postMessageHandler } = props;
  const isV2 = props.uiVersion !== 'v1';
  const [isExpanded, setIsExpanded] = React.useState(
    () => window.innerWidth > EXPANDED_WIDTH_THRESHOLD
  );

  React.useEffect(() => {
    // The parent may already be expanded, or resize without a button click.
    const updateLayoutState = () => {
      const expanded = window.innerWidth > EXPANDED_WIDTH_THRESHOLD;
      document.body.classList.toggle('chat-expanded', expanded);
      setIsExpanded(expanded);
    };
    updateLayoutState();
    window.addEventListener('resize', updateLayoutState);
    window.addEventListener('orientationchange', updateLayoutState);

    return () => {
      window.removeEventListener('resize', updateLayoutState);
      window.removeEventListener('orientationchange', updateLayoutState);
    };
  }, []);

  const minimizeEventHandler = () => {
    postMessageHandler({ msgType: OUTGOING_MESSAGES_TYPE.MINIMIZE_WINDOW });
  };

  const expandEventHandler = () => {
    const next = !document.body.classList.contains('chat-expanded');
    setIsExpanded(next);
    document.body.classList.toggle('chat-expanded', next);
    postMessageHandler({ msgType: OUTGOING_MESSAGES_TYPE.EXPAND_WINDOW });
  };

  const closeConversationEventHandler = () => {
    // postMessageHandler({ msgType: OUTGOING_MESSAGES_TYPE.CONV_CLOSED });
    window.dispatchEvent( new Event('closeOoO') );
    window.dispatchEvent( new Event('toggleDialogueBox') );
  };  

  return (
    <div className="header" style={isV2 ? { backgroundColor: '#ffffff' } : {}}>
        <div id="title-bar" className="header" style={isV2 ? { height: '60px', overflow: 'visible', backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', boxSizing: 'border-box' } : { height: '48px', overflow: 'hidden' }}>
        {isV2 && (
          <span style={{
            fontFamily: 'Adobe Clean, sans-serif', fontWeight: 700, fontSize: '14px', color: '#000000',
          }}
          >
            Adobe Virtual Assistant
          </span>
        )}
        <div id="adobe-icon" className={isV2 ? '' : 'icon'} style={isV2 ? { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' } : {}}>
          <Flex id="Flex" direction="row" height="size-450" alignItems="center">
            <TooltipTrigger>
              <button
                type="button"
                id="MinimizeConversation"
                onClick={minimizeEventHandler}
                aria-label={GlobalData.translatedStrings['app.header.minimize']}
                style={isV2 ? { color: '#000000', marginBottom: 0 } : {}}
              >
                <svg id="Close" className="Close" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" height="18" viewBox="0 0 18 18" width="18">
                  <rect id="Canvas" fill="#ff13dc" opacity="0" width="18" height="18" />
                  <path fill="currentColor" d="M4,7.01a1,1,0,0,1,1.7055-.7055l3.289,3.286,3.289-3.286a1,1,0,0,1,1.437,1.3865l-.0245.0245L9.7,11.7075a1,1,0,0,1-1.4125,0L4.293,7.716A.9945.9945,0,0,1,4,7.01Z" />
                </svg>
              </button>
              <Tooltip>{GlobalData.translatedStrings['app.header.minimize']}</Tooltip>
            </TooltipTrigger>
            {isV2 && (<TooltipTrigger>
              <button
                type="button"
                id="ExpandConversation"
                onClick={expandEventHandler}
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
                style={{ color: '#000000', marginBottom: 0 }}
              >
                {isExpanded ? (
                  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" height="14" width="14" viewBox="0 0 36 36">
                    <path fill="currentColor" d="M32.077 2.707a1 1 0 0 0-1.414 0l-6.484 6.484L21.2 6.206A.688.688 0 0 0 20.705 6a.7.7 0 0 0-.7.7v8.84a.5.5 0 0 0 .459.459H29.3a.7.7 0 0 0 .7-.7.685.685 0 0 0-.207-.49l-2.984-2.984 6.484-6.484a1 1 0 0 0 0-1.414zM15.541 20H6.7a.7.7 0 0 0-.7.7.685.685 0 0 0 .207.49l2.984 2.984-6.484 6.489a1 1 0 0 0 0 1.414l1.216 1.216a1 1 0 0 0 1.414 0l6.484-6.484 2.984 2.985A.688.688 0 0 0 15.3 30a.7.7 0 0 0 .7-.7v-8.84a.5.5 0 0 0-.459-.46z"/>
                  </svg>
                ) : (
                  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" height="14" width="14" viewBox="0 0 36 36">
                    <path fill="currentColor" d="M14.077 20.707a1 1 0 0 0-1.414 0l-6.484 6.484L3.2 24.206A.688.688 0 0 0 2.705 24a.7.7 0 0 0-.7.7v8.84a.5.5 0 0 0 .454.46H11.3a.7.7 0 0 0 .7-.7.685.685 0 0 0-.207-.49l-2.984-2.989 6.484-6.484a1 1 0 0 0 0-1.414zM33.541 2H24.7a.7.7 0 0 0-.7.705.685.685 0 0 0 .207.49l2.984 2.984-6.484 6.484a1 1 0 0 0 0 1.414l1.216 1.216a1 1 0 0 0 1.414 0l6.484-6.484 2.984 2.985A.688.688 0 0 0 33.3 12a.7.7 0 0 0 .7-.7V2.459A.5.5 0 0 0 33.541 2z"/>
                  </svg>
                )}
              </button>
              <Tooltip>{isExpanded ? 'Collapse' : 'Expand'}</Tooltip>
            </TooltipTrigger>)}
            {props.showClose && (<TooltipTrigger>
              <button
                type="button"
                id="CloseConversation"
                onClick={closeConversationEventHandler}
                aria-label={GlobalData.translatedStrings['app.header.close']}
                style={isV2 ? { color: '#000000', marginBottom: 0 } : {}}
              >
                {isV2 ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_194_601)">
                      <mask id="mask0_194_601" style={{maskType:'alpha'}} maskUnits="userSpaceOnUse" x="4" y="4" width="16" height="16">
                        <path d="M12.8485 12L17.0137 7.83517C17.2481 7.60079 17.2481 7.2211 17.0137 6.98673C16.7793 6.75235 16.3996 6.75235 16.1652 6.98673L12 11.1516L7.83477 6.98673C7.60039 6.75235 7.2207 6.75235 6.98633 6.98673C6.75195 7.2211 6.75195 7.60079 6.98633 7.83517L11.1515 12L6.98633 16.1648C6.75195 16.3992 6.75195 16.7789 6.98633 17.0133C7.10351 17.1305 7.25703 17.1891 7.41054 17.1891C7.56406 17.1891 7.71758 17.1305 7.83476 17.0133L12 12.8484L16.1652 17.0133C16.2824 17.1305 16.4359 17.1891 16.5894 17.1891C16.743 17.1891 16.8965 17.1305 17.0137 17.0133C17.248 16.7789 17.248 16.3992 17.0137 16.1648L12.8485 12Z" fill="#292929"/>
                      </mask>
                      <g mask="url(#mask0_194_601)">
                        <rect x="4" y="4" width="16" height="16" fill="#292929"/>
                      </g>
                    </g>
                    <defs>
                      <clipPath id="clip0_194_601">
                        <rect width="24" height="24" rx="7" fill="white"/>
                      </clipPath>
                    </defs>
                  </svg>
                ) : (
                  <span id="Close" className="Close" aria-hidden="true">✕</span>
                )}
              </button>
              <Tooltip>{GlobalData.translatedStrings['app.header.close']}</Tooltip>
            </TooltipTrigger>)}
          </Flex>
        </div>
      </div>
    </div>
  );
}
Header.propTypes = {
  postMessageHandler: PropTypes.func.isRequired,
  showClose: PropTypes.bool.isRequired,
  uiVersion: PropTypes.string,
};
